import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useEntitlements } from '../../context/EntitlementsContext';
import {
  LayoutDashboard, Users, CalendarDays, Megaphone, FileText,
  Church, Users2, DollarSign, Heart, MessageSquare,
  GraduationCap, Settings, LogOut, CreditCard, LifeBuoy, BarChart3, ShieldCheck, HandHeart,
  Flag, Wallet, Sprout, BookOpen, Lock, Receipt, Banknote, RefreshCw, Check, ChevronsUpDown, Sparkles, Wand2
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Badge } from '../ui/badge';

const allNavItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', module: 'dashboard' },
  { to: '/members', icon: Users, label: 'Miembros', module: 'members' },
  { to: '/events', icon: CalendarDays, label: 'Eventos', module: 'events' },
  { to: '/announcements', icon: Megaphone, label: 'Anuncios', module: 'announcements' },
  { to: '/posts', icon: FileText, label: 'Noticias', module: 'posts' },
  { to: '/ministries', icon: Church, label: 'Ministerios', module: 'ministries' },
  { to: '/groups', icon: Users2, label: 'Grupos', module: 'groups' },
  { to: '/courses', icon: GraduationCap, label: 'Cursos', module: 'courses' },
  { to: '/contributions', icon: DollarSign, label: 'Aportes', module: 'contributions' },
  { to: '/wallet', icon: Wallet, label: 'Wallet', module: 'wallet', addon: 'wallet_dispersion' },
  { to: '/prayers', icon: Heart, label: 'Oraciones', module: 'prayers' },
  { to: '/petitions', icon: MessageSquare, label: 'Peticiones', module: 'petitions' },
  { to: '/mentorships', icon: Sprout, label: 'Mentorías', module: 'mentorships' },
  { to: '/moderation', icon: Flag, label: 'Moderación', module: 'moderation' },
  { to: '/analytics', icon: BarChart3, label: 'Analíticas', module: 'analytics', addon: 'analiticas_avanzadas' },
  { to: '/team', icon: ShieldCheck, label: 'Equipo', module: 'team' },
  { to: '/apoyo-pastoral', icon: HandHeart, label: 'Tu Aliado Pastoral', module: 'apoyo_pastoral', addon: 'aliado_pastoral' },
  { to: '/help', icon: BookOpen, label: 'Centro de Ayuda', module: 'support' },
];

const adminNavItems = [
  { to: '/comunidad', icon: Sparkles, label: 'Comunidad Tu Iglesia' },
  { to: '/comunidad/contenido', icon: Wand2, label: 'Agente de Contenido' },
];

const roleLabels = {
  admin: 'Admin global',
  pastor: 'Pastor',
  member: 'Colaborador',
};

const roleBadgeColors = {
  admin: 'bg-purple-100 text-purple-700',
  pastor: 'bg-blue-100 text-blue-700',
  member: 'bg-green-100 text-green-700',
};

export function Sidebar({ churches = [], churchInfo = null, inSheet = false,
                          churchesLoading = false, onReloadChurches }) {
  const { user, logout, selectedChurchId, switchChurch, isSuperAdmin, canRead, roleType } = useAuth();
  const { hasModule } = useEntitlements();
  const location = useLocation();
  const [churchPickerOpen, setChurchPickerOpen] = React.useState(false);

  const selectedChurchName = selectedChurchId
    ? (churches.find(c => String(c.id) === String(selectedChurchId))?.name || churchInfo?.name || 'Iglesia seleccionada')
    : 'Todas las iglesias';

  const pickChurch = (id) => {
    switchChurch(id);
    setChurchPickerOpen(false);
  };

  // Filter nav items based on permissions
  const visibleNavItems = [
    ...allNavItems.filter(item => canRead(item.module)),
    ...(isSuperAdmin ? adminNavItems : []),
  ];

  return (
    <aside
      className={`${inSheet ? 'flex w-full' : 'hidden lg:flex w-[280px]'} flex-col min-h-screen bg-[#062B36] text-white/90 border-r border-white/10`}
      data-testid={inSheet ? 'sidebar-mobile' : 'sidebar'}
    >
      {/* Logo */}
      <div className="p-5 flex items-center gap-3 border-b border-white/10">
        <img
          src="https://customer-assets.emergentagent.com/job_pastor-dashboard-2/artifacts/w9o2gudq_LOGO%20Iglesia%20Blanco%203.png"
          alt="Tu Iglesia App"
          className="h-10 w-auto"
        />
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-white">Suite Ministerial</span>
          <span className="text-[10px] text-white/50">Tu Iglesia App</span>
        </div>
      </div>

      {/* Church Selector (SuperAdmin only) */}
      {isSuperAdmin && (
        <div className="px-4 py-3 border-b border-white/10">
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] uppercase tracking-wider text-white/50">Iglesia</label>
            {onReloadChurches && (
              <button
                onClick={() => onReloadChurches()}
                className="text-white/50 hover:text-white p-0.5"
                title="Recargar listado de iglesias"
                data-testid="church-selector-reload"
              >
                <RefreshCw className={`w-3 h-3 ${churchesLoading ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>
          <Popover open={churchPickerOpen} onOpenChange={setChurchPickerOpen}>
            <PopoverTrigger asChild>
              <button
                role="combobox"
                aria-expanded={churchPickerOpen}
                className="flex w-full items-center justify-between gap-2 rounded-md bg-white/5 border border-white/10 px-3 h-9 text-sm text-white hover:bg-white/10"
                data-testid="church-selector"
              >
                <span className="truncate text-left">{selectedChurchName}</span>
                <ChevronsUpDown className="w-3.5 h-3.5 shrink-0 opacity-60" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="p-0 w-[min(320px,calc(100vw-2rem))]" data-testid="church-picker-content">
              <Command>
                <CommandInput placeholder="Buscar iglesia…" data-testid="church-search-input" />
                <CommandList className="max-h-[320px]">
                  <CommandEmpty>Ninguna iglesia con ese nombre.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="Todas las iglesias" onSelect={() => pickChurch(null)} data-testid="church-option-all">
                      <Check className={`mr-2 h-4 w-4 ${!selectedChurchId ? 'opacity-100' : 'opacity-0'}`} />
                      Todas las iglesias
                    </CommandItem>
                    {churches.length === 0 && selectedChurchId && churchInfo?.name && (
                      <CommandItem value={churchInfo.name} onSelect={() => setChurchPickerOpen(false)}>
                        <Check className="mr-2 h-4 w-4" />
                        <span className="truncate">{churchInfo.name}</span>
                      </CommandItem>
                    )}
                    {churches.map(c => (
                      <CommandItem
                        key={c.id}
                        value={`${c.name} ${c.id}`}
                        onSelect={() => pickChurch(String(c.id))}
                        data-testid={`church-option-${c.id}`}
                      >
                        <Check className={`mr-2 h-4 w-4 shrink-0 ${String(selectedChurchId) === String(c.id) ? 'opacity-100' : 'opacity-0'}`} />
                        <span className="truncate">{c.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {churches.length === 0 && (
            <p className="text-[10px] text-white/40 mt-1" data-testid="church-selector-status">
              {churchesLoading ? 'Cargando iglesias…' : 'No se pudo cargar el listado. Toca el ícono para reintentar.'}
            </p>
          )}
        </div>
      )}

      {/* Church info (logo + name) when a church is selected */}
      {churchInfo && churchInfo.name && (
        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
          {churchInfo.logoUrl ? (
            <img
              src={churchInfo.logoUrl}
              alt={churchInfo.name}
              className="w-9 h-9 rounded-lg object-cover border border-white/10 shrink-0"
            />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center text-white/60 text-sm font-semibold shrink-0">
              {churchInfo.name[0]}
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate">{churchInfo.name}</p>
            <p className="text-[10px] text-white/40 truncate">Iglesia activa</p>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto sidebar-nav py-3 px-3 space-y-0.5">
        <span className="text-[10px] uppercase tracking-wider text-white/50 px-3 mb-2 block">Menú principal</span>
        {visibleNavItems.map((item) => {
          const Icon = item.icon;
          const locked = item.addon && !hasModule(item.addon);
          const isActive = location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(item.to));
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive
                  ? 'bg-white/10 text-white ring-1 ring-white/10'
                  : 'text-white/70 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Icon className="w-[18px] h-[18px] shrink-0" />
              <span className={locked ? 'text-white/45' : ''}>{item.label}</span>
              {locked && (
                <Lock
                  className="w-3.5 h-3.5 ml-auto text-[#C58B2A]"
                  data-testid={`sidebar-locked-${item.module}`}
                />
              )}
            </NavLink>
          );
        })}

        {(canRead('settings') || canRead('subscriptions') || canRead('dashboard')) && (
          <div className="mt-6">
            <span className="text-[10px] uppercase tracking-wider text-white/50 px-3 mb-2 block">Administración</span>
            {canRead('dashboard') && (
              <NavLink
                to="/billing"
                data-testid="sidebar-nav-suscripcion"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  location.pathname === '/billing'
                    ? 'bg-white/10 text-white ring-1 ring-white/10'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Receipt className="w-[18px] h-[18px]" />
                <span>Suscripción y pagos</span>
              </NavLink>
            )}
            {canRead('settings') && (
              <NavLink
                to="/settings"
                data-testid="sidebar-nav-settings"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  location.pathname === '/settings'
                    ? 'bg-white/10 text-white ring-1 ring-white/10'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Settings className="w-[18px] h-[18px]" />
                <span>Configuración</span>
              </NavLink>
            )}
            {canRead('subscriptions') && (
              <NavLink
                to="/subscriptions"
                data-testid="sidebar-nav-suscripciones"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  location.pathname === '/subscriptions'
                    ? 'bg-white/10 text-white ring-1 ring-white/10'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
              >
                <CreditCard className="w-[18px] h-[18px]" />
                <span>Suscripciones</span>
              </NavLink>
            )}
            {canRead('subscriptions') && isSuperAdmin && (
              <NavLink
                to="/collections"
                data-testid="sidebar-nav-cobranza"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  location.pathname === '/collections'
                    ? 'bg-white/10 text-white ring-1 ring-white/10'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Banknote className="w-[18px] h-[18px]" />
                <span>Cobranza</span>
              </NavLink>
            )}
            {canRead('support') && (
              <NavLink
                to="/support"
                data-testid="sidebar-nav-soporte"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  location.pathname === '/support'
                    ? 'bg-white/10 text-white ring-1 ring-white/10'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
              >
                <LifeBuoy className="w-[18px] h-[18px]" />
                <span>Soporte</span>
              </NavLink>
            )}
          </div>
        )}
      </nav>

      {/* User */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#00CCFF]/20 flex items-center justify-center text-sm font-semibold text-[#00CCFF]">
            {(user?.firstname || user?.name || 'U')[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.firstname || user?.name || 'Usuario'} {user?.lastname || ''}</p>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium ${roleBadgeColors[roleType] || 'bg-gray-100 text-gray-700'}`}>
              {roleLabels[roleType] || roleType}
            </span>
          </div>
          <button onClick={logout} className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-white" data-testid="sidebar-logout" aria-label="Cerrar sesión" title="Cerrar sesión">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
